import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, ChatCircle, Share, GameController, MapPin, Image, PaperPlaneTilt,
  Lightning, Trophy, Storefront, Users, MagnifyingGlass, Fire, CalendarDots,
  DotsThreeVertical, Trash, X, Plus
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { NotificationBell } from '../components/NotificationBell';
import { ActivationChecklist } from './OnboardingWizard';
import { WelcomeBackNudge } from '../components/WelcomeBackNudge';
import { UserBadgeList } from '../components/UserBadge';
import { ContextualTour, EmptyStateCard } from '../components/ContextualEducation';
import { Logo } from '../components/Logo';
import { toast } from 'sonner';
import { ensureFreshCoords, getPermissionState, requestAndFetchLocation } from '../lib/locationService';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API, withCredentials: true });

export default function DiscoverPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('feed');
  const [postContent, setPostContent] = useState('');
  const [postGame, setPostGame] = useState('');
  const [posting, setPosting] = useState(false);
  const [showCompose, setShowCompose] = useState(false);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ax().get('/api/social/feed');
      setFeed(Array.isArray(data) ? data : []);
    } catch { setFeed([]); }
    setLoading(false);
  }, []);

  const [locState, setLocState] = useState({ coords: null, permission: getPermissionState() });

  const loadEvents = useCallback(async (coordsArg) => {
    try {
      // Show all public/nearby events — sessions, shop events, tournaments.
      // Previously sliced to 5 which hid most public games. The "Happening
      // Nearby" carousel still slices its own visual subset below.
      const coords = coordsArg || locState.coords;
      const qs = new URLSearchParams({ feed_type: 'all', limit: '50' });
      if (coords && typeof coords.lat === 'number' && typeof coords.lon === 'number') {
        qs.set('user_lat', String(coords.lat));
        qs.set('user_lon', String(coords.lon));
      }
      const { data } = await ax().get(`/api/discover/feed?${qs.toString()}`);
      setEvents(Array.isArray(data) ? data.filter(e => e.type !== 'opponent') : []);
    } catch { setEvents([]); }
  }, [locState.coords]);

  // Pull the user's friends so we can split the Nearby feed into
  // "Friends Activity" vs "Nearby Players" (Part 7 of the launch audit).
  const [friendIds, setFriendIds] = useState([]);
  const loadFriends = useCallback(async () => {
    try {
      const { data } = await ax().get('/api/friends');
      const ids = new Set();
      (Array.isArray(data?.friends) ? data.friends : []).forEach(f => f.id && ids.add(f.id));
      (Array.isArray(data) ? data : []).forEach(f => f.id && ids.add(f.id));
      setFriendIds(Array.from(ids));
    } catch { setFriendIds([]); }
  }, []);

  useEffect(() => { loadFeed(); loadEvents(); loadFriends(); }, [loadFeed, loadEvents, loadFriends]);

  // On mount: request GPS permission + fetch fresh coords, then reload the
  // Nearby feed so the 25-mile radius filter uses the device location instead
  // of the (potentially stale) profile city/zip.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const coords = await ensureFreshCoords({ silent: false });
      if (cancelled) return;
      setLocState({ coords: coords || null, permission: getPermissionState() });
      if (coords) loadEvents(coords);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryLocation = async () => {
    const coords = await requestAndFetchLocation({ silent: false });
    setLocState({ coords: coords || null, permission: getPermissionState() });
    if (coords) {
      toast.success('Location updated — showing events within 25 miles');
      loadEvents(coords);
    } else {
      toast.error('Location permission is off. Enable it in Settings to see nearby events.');
    }
  };

  const submitPost = async () => {
    if (!postContent.trim()) return;
    setPosting(true);
    try {
      await ax().post('/api/social/posts', {
        content: postContent.trim(),
        game_name: postGame || undefined,
        post_type: 'text',
      });
      setPostContent('');
      setPostGame('');
      setShowCompose(false);
      toast.success('Posted!');
      loadFeed();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to post'); }
    setPosting(false);
  };

  const toggleLike = async (postId) => {
    try {
      const { data } = await ax().post(`/api/social/posts/${postId}/react`, { reaction_type: 'like' });
      setFeed(prev => prev.map(p => p.id === postId ? {
        ...p,
        liked_by_me: data.liked,
        likes_count: data.liked ? p.likes_count + 1 : p.likes_count - 1,
      } : p));
    } catch { toast.error('Failed'); }
  };

  const deletePost = async (postId) => {
    try {
      await ax().delete(`/api/social/posts/${postId}`);
      setFeed(prev => prev.filter(p => p.id !== postId));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  const inputCls = "w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-4 py-3 text-white text-sm placeholder:text-[#444] focus:outline-none focus:border-[#9D4CDD]/40 resize-none";

  return (
    <ContextualTour tourKey="discover_page">
    <div className="app-container" data-testid="discover-page">
      {/* Branded top strip */}
      <div className="px-6 pt-5 pb-1 flex items-center justify-between">
        <Logo variant="wordmark" size="xs" clickable={true} />
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/shops')} className="text-[#71717A] hover:text-white transition-colors" aria-label="Search shops">
            <MagnifyingGlass size={18} />
          </button>
          <NotificationBell />
        </div>
      </div>

      {/* Header */}
      <header className="px-6 pt-2 pb-3">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Discover</h1>
            <p className="text-[#71717A] text-xs">See what the community is playing</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white/[0.03] rounded-xl p-1">
          {[
            { key: 'feed', label: 'Feed', icon: Users },
            { key: 'nearby', label: 'Nearby', icon: MapPin },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
                tab === t.key ? 'bg-[#9D4CDD]/10 text-[#9D4CDD]' : 'text-[#71717A]'
              }`} data-testid={`tab-${t.key}`}>
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <div className="px-6 pb-36 space-y-4">
        {/* Welcome-back nudge — only shown to new users (< 24h old) with incomplete tasks */}
        <WelcomeBackNudge />
        {/* Activation Checklist */}
        <ActivationChecklist />

        {tab === 'feed' && (
          <>
            {/* Compose */}
            <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-4" data-testid="compose-area">
              {!showCompose ? (
                <button onClick={() => setShowCompose(true)}
                  className="w-full flex items-center gap-3 text-left" data-testid="compose-trigger">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-white text-xs font-bold">
                    {user?.name?.[0] || '?'}
                  </div>
                  <span className="text-[#555] text-sm flex-1">What are you playing?</span>
                  <Image size={18} className="text-[#333]" />
                </button>
              ) : (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}>
                  <textarea className={inputCls} rows={3} placeholder="Share what you're playing, a game recap, or ask who's up for a session..."
                    value={postContent} onChange={e => setPostContent(e.target.value)} autoFocus data-testid="compose-input" />
                  <div className="flex items-center gap-2 mt-2">
                    <input className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-1.5 text-white text-xs placeholder:text-[#444] focus:outline-none"
                      placeholder="Game name (optional)" value={postGame} onChange={e => setPostGame(e.target.value)} data-testid="compose-game" />
                    <button onClick={() => { setShowCompose(false); setPostContent(''); }}
                      className="p-2 text-[#555] hover:text-white"><X size={16} /></button>
                    <button onClick={submitPost} disabled={posting || !postContent.trim()}
                      className="px-4 py-1.5 rounded-lg bg-[#9D4CDD] text-white text-xs font-semibold disabled:opacity-40"
                      data-testid="compose-submit">
                      <PaperPlaneTilt size={14} className="inline mr-1" /> Post
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Nearby Events Broadcast (horizontal scroll) */}
            {Array.isArray(events) && events.length > 0 && (
              <div>
                <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                  <Fire size={14} weight="fill" className="text-[#F97316]" /> Happening Nearby
                </h3>
                <div className="flex gap-2 overflow-x-auto -mx-6 px-6 pb-2 scrollbar-none">
                  {events.slice(0, 8).map((evt, i) => {
                    const icons = { session: Users, shop_event: Storefront, tournament: Trophy };
                    const colors = { session: '#00F0FF', shop_event: '#9D4CDD', tournament: '#FACC15' };
                    const Icon = icons[evt.type] || Lightning;
                    const color = colors[evt.type] || '#00F0FF';
                    return (
                      <button key={`${evt.type}-${evt.id}-${i}`} onClick={() => {
                        if (evt.type === 'session') navigate(`/events/${evt.id}`);
                        else if (evt.type === 'tournament') navigate(`/tournaments/${evt.id}`);
                        else if (evt.type === 'shop_event') navigate(`/shops/${evt.shop_id || evt.id}`);
                      }} className="min-w-[160px] bg-[#0e0e16] border border-white/[0.06] rounded-xl p-3 text-left shrink-0 hover:border-white/[0.12] transition-colors"
                        data-testid={`nearby-${i}`}>
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Icon size={12} weight="duotone" style={{ color }} />
                          <span className="text-[9px] uppercase tracking-wider font-semibold" style={{ color }}>{evt.type?.replace('_', ' ')}</span>
                        </div>
                        <p className="text-white text-xs font-medium truncate">{evt.title}</p>
                        <p className="text-[#555] text-[10px] mt-0.5 truncate">{evt.game || ''} {evt.location ? `· ${evt.location}` : ''}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Social Feed */}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <div key={i} className="h-32 bg-white/[0.03] rounded-xl animate-pulse" />)}
              </div>
            ) : feed.length > 0 ? (
              <div className="space-y-3">
                {feed.map(post => (
                  <SocialPost key={post.id} post={post} user={user} onLike={toggleLike} onDelete={deletePost} onNavigate={navigate} />
                ))}
              </div>
            ) : (
              <EmptyStateCard type="discover_feed" />
            )}
          </>
        )}

        {tab === 'nearby' && (
          <>
            {locState.permission === 'denied' && (
              <div className="mb-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-3 flex items-start gap-3" data-testid="location-denied-banner">
                <MapPin size={18} weight="duotone" className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-white text-xs font-semibold">Location permission is off</p>
                  <p className="text-[#A1A1AA] text-[11px] mt-0.5">
                    We're showing events based on your profile city ({user?.location_city || user?.location || 'not set'}). Enable location for accurate 25-mile results.
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button onClick={retryLocation} className="text-[10px] px-3 py-1.5 rounded-md bg-[#F59E0B]/20 text-[#F59E0B] hover:bg-[#F59E0B]/30" data-testid="retry-location-btn">
                      Enable location
                    </button>
                    <button onClick={() => navigate('/profile')} className="text-[10px] px-3 py-1.5 rounded-md bg-white/5 text-[#A1A1AA] hover:bg-white/10" data-testid="edit-profile-location-btn">
                      Edit my city
                    </button>
                  </div>
                </div>
              </div>
            )}
            {(() => {
              // Split feed: Friends Activity vs Nearby Strangers.
              // Friend matches by host_id ∈ friendIds.
              const friendSet = new Set(friendIds);
              const isMine = (e) => e.host_id === user?.id;
              const friends = events.filter(e => !isMine(e) && friendSet.has(e.host_id));
              const strangers = events.filter(e => !isMine(e) && !friendSet.has(e.host_id));

              const card = (evt, i, sectionKey) => {
                const icons = { session: Users, shop_event: Storefront, tournament: Trophy };
                const colors = { session: '#00F0FF', shop_event: '#9D4CDD', tournament: '#FACC15' };
                const Icon = icons[evt.type] || Lightning;
                const color = colors[evt.type] || '#00F0FF';
                return (
                  <motion.div key={`${sectionKey}-${evt.type}-${evt.id}-${i}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-4 cursor-pointer hover:border-white/[0.12] transition-colors"
                    onClick={() => {
                      if (evt.type === 'session') navigate(`/events/${evt.id}`);
                      else if (evt.type === 'tournament') navigate(`/tournaments/${evt.id}`);
                      else if (evt.type === 'shop_event') navigate(`/shops/${evt.shop_id || evt.id}`);
                    }} data-testid={`${sectionKey}-card-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={16} weight="duotone" style={{ color }} />
                      <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color }}>{evt.type?.replace('_', ' ')}</span>
                      {evt.time && <span className="text-[10px] text-[#555] ml-auto"><CalendarDots size={10} className="inline" /> {evt.time}</span>}
                    </div>
                    <h3 className="text-white font-semibold text-sm">{evt.title}</h3>
                    {evt.description && <p className="text-[#888] text-xs mt-1 line-clamp-2">{evt.description}</p>}
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-[#555]">
                      {evt.game && <span><GameController size={10} className="inline" /> {evt.game}</span>}
                      {evt.location && <span><MapPin size={10} className="inline" /> {evt.location}</span>}
                      {evt.open_slots !== undefined && evt.open_slots !== null && <span>{evt.open_slots} spots</span>}
                    </div>
                  </motion.div>
                );
              };

              if (events.length === 0) return <EmptyStateCard type="discover_feed" />;

              return (
                <div className="space-y-6" data-testid="nearby-sections">
                  <section data-testid="section-friends-activity">
                    <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                      <Users size={14} weight="duotone" className="text-[#39FF14]" /> Friends Activity
                      <span className="text-[10px] text-[#555] ml-1">{friends.length}</span>
                    </h3>
                    {friends.length > 0 ? (
                      <div className="space-y-3">{friends.map((e, i) => card(e, i, 'friend'))}</div>
                    ) : (
                      <p className="text-[#444] text-xs">No friends hosting games right now. Add friends to see their events here.</p>
                    )}
                  </section>
                  <section data-testid="section-nearby-players">
                    <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                      <MapPin size={14} weight="duotone" className="text-[#00F0FF]" /> Nearby Players
                      <span className="text-[10px] text-[#555] ml-1">{strangers.length}</span>
                    </h3>
                    {strangers.length > 0 ? (
                      <div className="space-y-3">{strangers.map((e, i) => card(e, i, 'stranger'))}</div>
                    ) : (
                      <p className="text-[#444] text-xs">No nearby public events found. Check back later or host one yourself.</p>
                    )}
                  </section>
                </div>
              );
            })()}
          </>
        )}
      </div>

      <BottomNav />
    </div>
    </ContextualTour>
  );
}

// ── Social Post Card ──

function SocialPost({ post, user, onLike, onDelete, onNavigate }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const isOwn = user?.id === post.user_id;

  const loadComments = async () => {
    try {
      const { data } = await ax().get(`/api/social/posts/${post.id}/comments`);
      setComments(data);
    } catch {}
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    try {
      await ax().post(`/api/social/posts/${post.id}/comments`, { content: commentText.trim() });
      setCommentText('');
      loadComments();
    } catch { toast.error('Failed'); }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl overflow-hidden" data-testid={`post-${post.id}`}>
      {/* Header */}
      <div className="flex items-center gap-3 p-4 pb-2">
        <button onClick={() => onNavigate(`/profile/${post.user_id}`)}
          className="w-9 h-9 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-white text-xs font-bold shrink-0">
          {post.user_name?.[0] || '?'}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => onNavigate(`/profile/${post.user_id}`)} className="text-white text-sm font-semibold hover:underline">
              {post.user_name}
            </button>
            <UserBadgeList badges={post.user_badges} size="sm" showLabel={false} />
          </div>
          <div className="flex items-center gap-2 text-[10px] text-[#555]">
            <span>@{post.username}</span>
            <span>{timeAgo(post.created_at)}</span>
            {post.game_name && <span className="text-[#9D4CDD]"><GameController size={10} className="inline" /> {post.game_name}</span>}
          </div>
        </div>
        {isOwn && (
          <div className="relative">
            <button onClick={() => setShowMenu(!showMenu)} className="text-[#555] hover:text-white p-1">
              <DotsThreeVertical size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 bg-[#1a1a1a] border border-white/[0.1] rounded-lg shadow-xl z-10">
                <button onClick={() => { onDelete(post.id); setShowMenu(false); }}
                  className="flex items-center gap-2 px-3 py-2 text-[#EF4444] text-xs hover:bg-white/[0.04] w-full">
                  <Trash size={12} /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{post.content}</p>
        {post.image_url && (
          <img src={post.image_url} alt="" className="mt-3 rounded-lg w-full max-h-72 object-cover" />
        )}
        {post.location && (
          <p className="text-[#555] text-[10px] mt-2"><MapPin size={10} className="inline" /> {post.location}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 px-4 py-2.5 border-t border-white/[0.04]">
        <button onClick={() => onLike(post.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            post.liked_by_me ? 'text-[#EF4444] bg-[#EF4444]/10' : 'text-[#555] hover:text-[#EF4444] hover:bg-[#EF4444]/5'
          }`} data-testid={`like-${post.id}`}>
          <Heart size={14} weight={post.liked_by_me ? 'fill' : 'regular'} />
          {post.likes_count > 0 && post.likes_count}
        </button>
        <button onClick={() => { setShowComments(!showComments); if (!showComments) loadComments(); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[#555] hover:text-[#00F0FF] hover:bg-[#00F0FF]/5 transition-colors"
          data-testid={`comment-btn-${post.id}`}>
          <ChatCircle size={14} />
          {post.comments_count > 0 && post.comments_count}
        </button>
      </div>

      {/* Comments */}
      <AnimatePresence>
        {showComments && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="border-t border-white/[0.04] px-4 py-3 space-y-2">
              {comments.map(c => (
                <div key={c.id} className="flex gap-2">
                  <div className="w-6 h-6 rounded-full bg-white/[0.06] flex items-center justify-center text-[9px] text-[#888] font-bold shrink-0">{c.user_name?.[0]}</div>
                  <div>
                    <span className="text-white text-xs font-semibold">{c.user_name}</span>
                    <span className="text-[#888] text-xs ml-1.5">{c.content}</span>
                  </div>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input className="flex-1 bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-1.5 text-white text-xs placeholder:text-[#444] focus:outline-none"
                  placeholder="Add a comment..." value={commentText} onChange={e => setCommentText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submitComment()} data-testid={`comment-input-${post.id}`} />
                <button onClick={submitComment} disabled={!commentText.trim()}
                  className="px-3 py-1.5 rounded-lg bg-[#00F0FF]/10 text-[#00F0FF] text-xs font-medium disabled:opacity-30">
                  Post
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
